import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import NavBar from '../components/NavBar';

const ADMIN_API = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5000';
const STORAGE_KEY = 'admin_key';

type StatusFilter = 'all' | 'failed' | 'permanent_failure';

interface Message {
  id: number;
  name: string;
  email: string;
  message: string;
  status: string;
  retry_count: number;
  error: string | null;
  created_at: number;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f0ede8',
    fontFamily: '"Inter", sans-serif',
    '& fieldset': { borderColor: 'rgba(240,237,232,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(240,237,232,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#c9a84c' },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(240,237,232,0.5)',
    fontFamily: '"Inter", sans-serif',
    '&.Mui-focused': { color: '#c9a84c' },
  },
};

const cellSx = {
  p: 1.5,
  borderBottom: '1px solid rgba(240,237,232,0.1)',
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.85rem',
  color: '#f0ede8',
  verticalAlign: 'top',
};

const FILTER_LABELS: Record<StatusFilter, string> = {
  all: 'All',
  failed: 'Failed',
  permanent_failure: 'Permanent Failure',
};

const AdminPage: React.FC = () => {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [keyInput, setKeyInput] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(false);

  const loadMessages = async (key: string, f: StatusFilter) => {
    setLoading(true);
    const url =
      f === 'all'
        ? `${ADMIN_API}/admin/messages`
        : `${ADMIN_API}/admin/messages?status=${f}`;
    try {
      const res = await fetch(url, { headers: { 'X-Admin-Key': key } });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch {
      // network error — table stays empty
    }
    setLoading(false);
  };

  const unlock = async () => {
    setUnlockError('');
    try {
      const res = await fetch(`${ADMIN_API}/admin/messages`, {
        headers: { 'X-Admin-Key': keyInput },
      });
      if (res.ok) {
        localStorage.setItem(STORAGE_KEY, keyInput);
        setAdminKey(keyInput);
      } else {
        setUnlockError('Invalid admin key');
      }
    } catch {
      setUnlockError('Could not reach server');
    }
  };

  const retry = async (id: number) => {
    await fetch(`${ADMIN_API}/admin/messages/${id}/retry`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey },
    });
    loadMessages(adminKey, filter);
  };

  useEffect(() => {
    if (adminKey) loadMessages(adminKey, filter);
  }, [adminKey, filter]);

  if (!adminKey) {
    return (
      <Box data-testid="admin-page" sx={{ minHeight: '100vh', backgroundColor: '#070b1c' }}>
        <NavBar />
        <Box sx={{ maxWidth: 400, mx: 'auto', pt: { xs: 18, md: 22 }, px: 3 }}>
          <Box sx={{ width: 40, height: 2, backgroundColor: '#c9a84c', mb: 3 }} />
          <Typography
            variant="h4"
            sx={{ mb: 4, fontFamily: '"Cormorant Garamond", serif', color: '#f0ede8', fontSize: { xs: '1.8rem', md: '2.2rem' } }}
          >
            Admin Access
          </Typography>
          <TextField
            label="Admin Key"
            type="password"
            fullWidth
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') unlock(); }}
            slotProps={{ htmlInput: { 'data-testid': 'admin-key-input' } }}
            sx={{ ...fieldSx, mb: 2 }}
          />
          {unlockError && (
            <Typography
              data-testid="admin-unlock-error"
              sx={{ color: '#e57373', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem', mb: 2 }}
            >
              {unlockError}
            </Typography>
          )}
          <Button
            data-testid="admin-unlock-btn"
            onClick={unlock}
            disabled={!keyInput}
            sx={{
              px: 4,
              py: 1.5,
              backgroundColor: '#c9a84c',
              color: '#070b1c',
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.8rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              borderRadius: 0,
              '&:hover': { backgroundColor: '#b8963e' },
              '&:disabled': { backgroundColor: 'rgba(201,168,76,0.3)', color: 'rgba(7,11,28,0.5)' },
            }}
          >
            Unlock
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="admin-page" sx={{ minHeight: '100vh', backgroundColor: '#070b1c' }}>
      <NavBar />
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 12 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: 40, height: 2, backgroundColor: '#c9a84c', mb: 3 }} />
        <Typography
          variant="h4"
          sx={{ mb: 4, fontFamily: '"Cormorant Garamond", serif', color: '#f0ede8', fontSize: { xs: '1.8rem', md: '2.5rem' } }}
        >
          Messages
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
          {(['all', 'failed', 'permanent_failure'] as StatusFilter[]).map((f) => (
            <Button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              sx={{
                color: filter === f ? '#070b1c' : '#c9a84c',
                backgroundColor: filter === f ? '#c9a84c' : 'transparent',
                border: '1px solid #c9a84c',
                borderRadius: 0,
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                px: 2,
                py: 0.75,
                '&:hover': {
                  backgroundColor: filter === f ? '#b8963e' : 'rgba(201,168,76,0.1)',
                },
              }}
            >
              {FILTER_LABELS[f]}
            </Button>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: '#c9a84c' }} />
          </Box>
        ) : (
          <Box
            data-testid="admin-message-table"
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
          >
            <Box component="thead">
              <Box component="tr">
                {['ID', 'Name', 'Email', 'Message', 'Status', 'Retries', 'Error', 'Date', ''].map((h) => (
                  <Box
                    key={h}
                    component="th"
                    sx={{
                      textAlign: 'left',
                      p: 1.5,
                      borderBottom: '1px solid rgba(201,168,76,0.4)',
                      color: '#c9a84c',
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.7rem',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {messages.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={9} sx={{ ...cellSx, color: 'rgba(240,237,232,0.4)', textAlign: 'center', py: 6 }}>
                    No messages
                  </Box>
                </Box>
              ) : (
                messages.map((m) => (
                  <Box component="tr" key={m.id} data-testid={`message-row-${m.id}`}>
                    <Box component="td" sx={cellSx}>{m.id}</Box>
                    <Box component="td" sx={cellSx}>{m.name}</Box>
                    <Box component="td" sx={{ ...cellSx, wordBreak: 'break-all' }}>{m.email}</Box>
                    <Box component="td" sx={{ ...cellSx, wordBreak: 'break-word' }}>{m.message}</Box>
                    <Box component="td" sx={cellSx}>{m.status}</Box>
                    <Box component="td" sx={cellSx}>{m.retry_count}</Box>
                    <Box component="td" sx={{ ...cellSx, color: '#e57373', wordBreak: 'break-word' }}>
                      {m.error || '—'}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      {new Date(m.created_at * 1000).toLocaleString()}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      <Button
                        data-testid={`retry-btn-${m.id}`}
                        onClick={() => retry(m.id)}
                        size="small"
                        sx={{
                          color: '#c9a84c',
                          border: '1px solid #c9a84c',
                          borderRadius: 0,
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          py: 0.5,
                          px: 1.5,
                          '&:hover': { backgroundColor: 'rgba(201,168,76,0.1)' },
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminPage;
